import { defineStore } from 'pinia';
import {
  Capture,
  CaptureDataSourceID,
  CaptureEvent,
  CaptureEventIds,
  CaptureEventTypes,
  PowerState,
  SktErrors,
  type AppInfo,
  type DeviceInfo,
} from 'socketmobile-capturejs';
import { TypedEmitter } from 'tiny-typed-emitter';
import { onMounted, onUnmounted, ref, type Ref } from 'vue';
import { usePiniaRefStore } from '../composables/usePiniaRefStore';
import { useNotifications } from './notificationStore';
import { useI18n } from 'vue-i18n';
import { useLocalStorage } from '@vueuse/core';

interface SocketScannerInputOptions {
  prepend?: boolean;

  onInput?: (value: string) => boolean | void;
}

export interface DecodedData {
  id: CaptureDataSourceID[keyof CaptureDataSourceID];
  name: string;
  data: number[];
}
export interface DiscoveredDevice {
  name: string;
  serviceUUID: string;
  identifierUUID: string;
}

export class SocketCaptureEvent<Id extends CaptureEventIds, Type extends CaptureEventTypes, T>
  extends Event
  implements Omit<CaptureEvent<T>, 'type'>
{
  id: Id;
  captureType: Type;
  result: number;
  value?: T;

  constructor(type: string, event: CaptureEvent<T>) {
    super(type);

    this.id = event.id as Id;
    this.captureType = event.type as Type;
    this.result = event.result;
    this.value = event.value as T;
  }
}

export type SocketScanDeviceArrivalEvent = SocketCaptureEvent<
  CaptureEventIds.DeviceArrival,
  CaptureEventTypes.DeviceInfo,
  DeviceInfo
>;
export type SocketScanDeviceRemovalEvent = SocketCaptureEvent<
  CaptureEventIds.DeviceRemoval,
  CaptureEventTypes.DeviceInfo,
  DeviceInfo
>;
export type SocketScanTerminateEvent = SocketCaptureEvent<
  CaptureEventIds.Terminate,
  CaptureEventTypes.None,
  void
>;
export type SocketScanErrorEvent = SocketCaptureEvent<
  CaptureEventIds.Error,
  CaptureEventTypes.Ulong,
  (typeof SktErrors)[keyof typeof SktErrors]
>;
export type SocketScanDecodedDataEvent = SocketCaptureEvent<
  CaptureEventIds.DecodedData,
  CaptureEventTypes.DecodedData,
  DecodedData
>;
export type SocketScanPowerEvent = SocketCaptureEvent<
  CaptureEventIds.Power,
  CaptureEventTypes.Ulong,
  PowerState
>;
export type SocketScanButtonsEvent = SocketCaptureEvent<
  CaptureEventIds.Buttons,
  CaptureEventTypes.Byte,
  number
>;
export type SocketScanBatteryLevelEvent = SocketCaptureEvent<
  CaptureEventIds.BatteryLevel,
  CaptureEventTypes.Ulong,
  number
>;
export type SocketScanListenerStartedEvent = SocketCaptureEvent<
  CaptureEventIds.ListenerStarted,
  CaptureEventTypes.None,
  void
>;
export type SocketScanDeviceOwnershipEvent = SocketCaptureEvent<
  CaptureEventIds.DeviceOwnership,
  CaptureEventTypes.String,
  string
>;
export type SocketScanDeviceManagerArrivalEvent = SocketCaptureEvent<
  CaptureEventIds.DeviceManagerArrival,
  CaptureEventTypes.DeviceInfo,
  DeviceInfo
>;
export type SocketScanDeviceManagerRemovalEvent = SocketCaptureEvent<
  CaptureEventIds.DeviceManagerRemoval,
  CaptureEventTypes.DeviceInfo,
  DeviceInfo
>;
export type SocketScanDeviceDiscoveredEvent = SocketCaptureEvent<
  CaptureEventIds.DeviceDiscovered,
  CaptureEventTypes.String,
  DiscoveredDevice
>;
export type SocketScanDiscoveryEndEvent = SocketCaptureEvent<
  CaptureEventIds.DiscoveryEnd,
  CaptureEventTypes.None,
  void
>;

interface SocketScanEmitterEvents {
  devicearrival: (event: SocketScanDeviceArrivalEvent) => void;
  deviceremoval: (event: SocketScanDeviceRemovalEvent) => void;
  terminate: (event: SocketScanTerminateEvent) => void;
  error: (event: SocketScanErrorEvent) => void;
  decodeddata: (event: SocketScanDecodedDataEvent) => void;
  power: (event: SocketScanPowerEvent) => void;
  buttons: (event: SocketScanButtonsEvent) => void;
  batterylevel: (event: SocketScanBatteryLevelEvent) => void;
  listenerstarted: (event: SocketScanListenerStartedEvent) => void;
  deviceownership: (event: SocketScanDeviceOwnershipEvent) => void;
  devicemanagerarrival: (event: SocketScanDeviceManagerArrivalEvent) => void;
  devicemanagerremoval: (event: SocketScanDeviceManagerRemovalEvent) => void;
  devicediscovered: (event: SocketScanDeviceDiscoveredEvent) => void;
  discoveryend: (event: SocketScanDiscoveryEndEvent) => void;
  '*': (event: SocketCaptureEvent<CaptureEventIds, CaptureEventTypes, any>) => void;
}

const EVENT_NAME_MAP: Record<CaptureEventIds, keyof SocketScanEmitterEvents> = {
  [CaptureEventIds.DeviceArrival]: 'devicearrival',
  [CaptureEventIds.DeviceRemoval]: 'deviceremoval',
  [CaptureEventIds.Terminate]: 'terminate',
  [CaptureEventIds.Error]: 'error',
  [CaptureEventIds.DecodedData]: 'decodeddata',
  [CaptureEventIds.Power]: 'power',
  [CaptureEventIds.Buttons]: 'buttons',
  [CaptureEventIds.BatteryLevel]: 'batterylevel',
  [CaptureEventIds.ListenerStarted]: 'listenerstarted',
  [CaptureEventIds.DeviceOwnership]: 'deviceownership',
  [CaptureEventIds.DeviceManagerArrival]: 'devicemanagerarrival',
  [CaptureEventIds.DeviceManagerRemoval]: 'devicemanagerremoval',
  [CaptureEventIds.DeviceDiscovered]: 'devicediscovered',
  [CaptureEventIds.DiscoveryEnd]: 'discoveryend',
  [CaptureEventIds.LogTrace]: '*',
  [CaptureEventIds.NotInitialized]: '*',
  [CaptureEventIds.LastID]: '*',
};

export const useSocketScannerStore = defineStore('socket-scanner', () => {
  const {
    VITE_SOCKET_APP_ID: appId = '',
    VITE_SOCKET_DEVELOPER_ID: developerId = '',
    VITE_SOCKET_APP_KEY: appKey = '',
  } = import.meta.env;

  const capture = new Capture();
  const captureDevice = ref<Capture | null>(null);
  const emitter = new TypedEmitter<SocketScanEmitterEvents>();

  const { t } = useI18n();
  const { addNotification } = useNotifications();
  const connection = ref(-1);
  const connecting = ref(false);
  const defaultDevice = useLocalStorage<null | DeviceInfo>('socket-scanner-default-device', null, {
    deep: true,
  });

  const onNotification = <T = any>(event: CaptureEvent<T>, handle?: number) => {
    if (!event || !event.id) return; // Ignore invalid events

    if (event.id === CaptureEventIds.DeviceArrival) {
      const { value } = event as CaptureEvent<DeviceInfo>;
      connectDevice(value!);
    }

    const eventName = EVENT_NAME_MAP[event.id];

    if (eventName !== '*') emitter.emit('*', new SocketCaptureEvent(eventName, event));
    emitter.emit(eventName, new SocketCaptureEvent(eventName, event));
  };

  const connect = async () => {
    if (connecting.value) return;

    connecting.value = true;
    connection.value = -1;

    const appInfo: AppInfo = { appId, developerId, appKey };

    try {
      connection.value = await capture.open(appInfo, onNotification);
    } catch (error) {
      const code = error as (typeof SktErrors)[keyof typeof SktErrors];
      console.error('An error occurred while connecting Socket companion app:', { error });

      switch (code) {
        case SktErrors.ESKT_UNABLEOPENDEVICE:
          addNotification({
            type: 'error',
            title: t('error.scanner-init-cant-open-device.title'),
            message: t('error.scanner-init-cant-open-device.message'),
          });
          break;
        default:
          // Disabled: Should be controlled by a setting
          // addNotification({
          //   type: "error",
          //   title: t("error.scanner-init-unknown.title"),
          //   message: t("error.scanner-init-unknown.message", { code }),
          // });
          break;
      }
    } finally {
      connecting.value = false;
    }
  };

  const connectDevice = async (device: DeviceInfo) => {
    captureDevice.value = new Capture();

    try {
      const id = await captureDevice.value!.openDevice(device.guid, capture);

      console.log('Connected to device:', { id, device });
      addNotification({
        type: 'success',
        title: t('notification.scanner-connected.title'),
        message: t('notification.scanner-connected.message', { deviceName: device.name }),
      });
    } catch (error) {
      const code = error as SktErrors[keyof SktErrors];
      console.error('An error occurred while connection Socket device:', { error });

      switch (code) {
        default:
          addNotification({
            type: 'error',
            title: t('error.scanner-connect-error.title'),
            message: t('error.scanner-connect-error.message', { code }),
          });
          break;
      }

      return null;
    }
  };

  const disconnectDevice = async () => {
    if (!captureDevice.value) return;

    await captureDevice.value.close();
    captureDevice.value = null;

    addNotification({
      type: 'warning',
      title: 'Scanner disconnected',
      message: 'The scanner has been successfully disconnected.',
    });
  };

  return {
    capture,
    captureDevice,
    connecting,
    emitter,
    defaultDevice,
    connect,
    connectDevice,
    disconnectDevice,
  };
});

export const useSocketScanner = (
  input?: Ref<string> | null,
  options?: SocketScannerInputOptions,
) => {
  const {
    capture,
    captureDevice,
    emitter,
    defaultDevice,
    connecting,
    connect,
    connectDevice,
    disconnectDevice,
  } = usePiniaRefStore(useSocketScannerStore());

  const connectScanRef = (input?: Ref<string> | null) => {
    const connectListener = (event: SocketScanDecodedDataEvent) => {
      const { value: { data } = {} } = event;
      const decodedData = data ? new TextDecoder().decode(Uint8Array.from(data)) : '';

      if (input) input.value = decodedData;

      if (options?.onInput) {
        const handleOthers = options.onInput(decodedData) ?? true;
        if (!handleOthers) event.stopImmediatePropagation();
      }
    };

    onMounted(() =>
      options?.prepend
        ? emitter.prependListener('decodeddata', connectListener)
        : emitter.addListener('decodeddata', connectListener),
    );
    onUnmounted(() => emitter.removeListener('decodeddata', connectListener));
  };

  onMounted(() => connect());

  if (input || options?.onInput) connectScanRef(input);

  return {
    capture,
    captureDevice,
    emitter,
    connecting,
    defaultDevice,
    connectScanRef,
    connect,
    connectDevice,
    disconnectDevice,
  };
};
